import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { MarketPerformanceGraphStyles as styles } from './market-performance-graph.styles';
import { MarketPerformanceGraphTemplate as template } from './market-performance-graph.template';
import Highcharts, { Chart } from 'highcharts';
import { IProjectHistoricalTs, IVppa } from '../../../../types';
import { getDatesFromData, getValuesFromData } from '../../../../utils/chart-data-utils';
import { CONTRACT_TYPE, GRAPH_PERIODICITY, logger } from '../../../../utils';
import { IVppaService } from '../../../../services/vppa.service';

const name = 'market-performance-graph';

@customElement({
  name,
  template,
  styles,
})
export class MarketPerformanceGraph extends FASTElement {
  @IVppaService vppaService: IVppaService;

  @observable historicalData: Array<IProjectHistoricalTs> = [];

  @observable marketPerformanceChart!: HTMLDivElement;
  @observable chart: Chart;
  @observable selectedContractType: string;
  @attr vppaId: string;
  private existingVppa: IVppa;

  connectedCallback() {
    super.connectedCallback();

    window.addEventListener('resize', () => {
      this.reflow();
    });
  }

  public vppaIdChanged() {
    if (this.vppaId) {
      this.vppaService.getVppaHistoricalTs(this.vppaId).then((response) => {
        if (response && response.MESSAGE_TYPE !== 'EVENT_NACK') {
          const minDate = new Date(new Date().setFullYear(new Date().getFullYear() - 3)).getTime();
          this.historicalData = response.REPLY.filter((data) => data.METRICS_DATETIME >= minDate).sort(
            (a, b) => Number(a.METRICS_DATETIME) - Number(b.METRICS_DATETIME)
          );

          setTimeout(() => {
            this.renderMarketPerformanceChart();
          }, 100);
        } else {
          this.historicalData = [];
          // TODO: Use a Toast to let the user know that something went wrong
          logger.error(response.ERROR);
        }
      });
    } else {
      this.historicalData = [];
    }
  }

  public reflow() {
    setTimeout(() => {
      if (this.chart) {
        this.renderMarketPerformanceChart();
        this.chart.reflow();
      }
    }, 0);
  }

  private async renderMarketPerformanceChart() {
    if (!this.historicalData || this.historicalData.length === 0) {
      return;
    }
    const response = await this.vppaService.getVppa(this.vppaId);
    if (response.REPLY) {
      this.existingVppa = response.REPLY[0];
    }
    const dates = getDatesFromData(this.historicalData, GRAPH_PERIODICITY.MONTHLY);
    if (this.existingVppa.CONTRACT_TYPE === CONTRACT_TYPE.REC) {
      const lmeAsgenHubUsdPerMwh = getValuesFromData(
        this.historicalData,
        'LME_ASGEN_NODE_MT_CO2E_PER_MWH',
        GRAPH_PERIODICITY.MONTHLY,
        null
      );
      this.chart = Highcharts.chart({
        chart: {
          renderTo: this.marketPerformanceChart,
        },
        title: {
          text: '',
        },
        xAxis: {
          categories: dates,
        },
        yAxis: [
          {
            title: {
              text: 'MT CO₂/MWh',
              rotation: -90,
              margin: 20,
            },
            labels: {
              format: '{value:,.2f}',
            },
          },
        ],
        tooltip: {
          valueDecimals: 2,
        },
        series: [
          {
            type: 'line',
            color: Highcharts.getOptions().colors[4],
            name: 'Zonal LME Generation Weighted - Historical (MT CO₂/MWh)',
            data: lmeAsgenHubUsdPerMwh,
            zIndex: 1,
          },
        ],
      });
    } else {
      const lmeAsgenHubUsdPerMwh = getValuesFromData(
          this.historicalData,
          'LME_ASGEN_NODE_MT_CO2E_PER_MWH',
          GRAPH_PERIODICITY.MONTHLY,
          null
      );
      const rollingAvgSixMonthGenHubUsdPerMwh = getValuesFromData(
        this.historicalData,
        'ROLLING_AVG_6_MONTH_GEN_HUB_USD_PER_MWH',
        GRAPH_PERIODICITY.MONTHLY,
        null
      );
      const priceAtcHubUsdPerMwh = getValuesFromData(
        this.historicalData,
        'PRICE_ATC_HUB_USD_PER_MWH',
        GRAPH_PERIODICITY.MONTHLY,
        null
      );
      const priceAsgenHubUsdPerMwh = getValuesFromData(
        this.historicalData,
        'PRICE_ASGEN_HUB_USD_PER_MWH',
        GRAPH_PERIODICITY.MONTHLY,
        null
      );

      this.chart = Highcharts.chart({
        chart: {
          renderTo: this.marketPerformanceChart,
        },
        title: {
          text: '',
        },
        xAxis: {
          categories: dates,
        },
        yAxis: [
          {
            // Primary yAxis
            title: {
              text: '$/MWh',
            },
            labels: {
              format: '${value:,.0f}',
            },
          },
          {
            // Secondary yAxis
            title: {
              text: 'MT CO₂/MWh',
              rotation: -90,
              margin: 20,
            },
            labels: {
              format: '{value:,.2f}',
            },
            opposite: true,
          },
        ],
        tooltip: {
          valueDecimals: 2,
        },
        series: [
          {
            type: 'line',
            dashStyle: 'Dot',
            color: Highcharts.getOptions().colors[3],
            yAxis: 0,
            name: '6 mo Rolling Avg. Generation Weighted Hub Price ($/MWh)',
            data: rollingAvgSixMonthGenHubUsdPerMwh,
            zIndex: 1,
          },
          {
            type: 'line',
            color: Highcharts.getOptions().colors[1],
            yAxis: 0,
            name: 'Hub Price ATC - Historical ($/MWh)',
            data: priceAtcHubUsdPerMwh,
            zIndex: 1,
          },
          {
            type: 'line',
            color: Highcharts.getOptions().colors[2],
            yAxis: 0,
            name: 'Generation Weighted Hub Price ($/MWh)',
            data: priceAsgenHubUsdPerMwh,
            zIndex: 1,
          },
          {
            type: 'line',
            color: Highcharts.getOptions().colors[4],
            name: 'Zonal LME Generation Weighted - Historical (MT CO₂/MWh)',
            data: lmeAsgenHubUsdPerMwh,
            yAxis: 1,
            zIndex: 1,
          },
        ],
      });
    }
  }
}
